import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ImageSyncItem {
  _id: string;
  sku: string;
  title: string;
  status: 'pending' | 'syncing' | 'success' | 'error';
  action?: 'created' | 'updated';
  error?: string;
  shopifyId?: string;
  imageUrl?: string;
  // Image fields
  dbImageProduction?: string;
  dbImageShopify?: string;
  shopifyImageUrl?: string;
  hasImageInDB?: boolean;
  hasImageInShopify?: boolean;
  imageInSync?: boolean;
  shopifyMediaCount?: number;
  // Price fields
  dbPrice?: number;
  shopifyPrice?: number;
  priceMatch?: boolean;
  priceDifference?: number;
  // Tag fields
  dbTags?: string[];
  shopifyTags?: string[];
  tagsMatch?: boolean;
  missingTagsInShopify?: string[];
  extraTagsInShopify?: string[];
  // Overall
  needsSync?: boolean;
}

export interface ImageSyncProgress {
  completed: number;
  total: number;
  percentage: number;
}

export interface ImageSyncStats {
  total: number;
  synced: number;
  pending: number;
  failed: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageSyncService {
  // Observables for real-time updates
  private progressSubject = new BehaviorSubject<ImageSyncProgress>({ completed: 0, total: 0, percentage: 0 });
  public progress$ = this.progressSubject.asObservable();

  private itemsSubject = new BehaviorSubject<ImageSyncItem[]>([]);
  public items$ = this.itemsSubject.asObservable();

  private statusSubject = new Subject<{ type: string; message?: string; data?: any }>();
  public status$ = this.statusSubject.asObservable();

  private statsSubject = new BehaviorSubject<ImageSyncStats>({ total: 0, synced: 0, pending: 0, failed: 0 });
  public stats$ = this.statsSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Check for image discrepancies (scans products)
   */
  checkDiscrepancies(store: string = 'BHS', limit: number = 100, offset: number = 0, skus?: string): Observable<any> {
    const params: any = { store, limit, offset };
    if (skus) {
      params.skus = skus;
    }

    // Initialize with empty state
    this.reset();

    return this.http.get(`${environment.API_PATH}/product/image-sync/check`, { params });
  }

  /**
   * Sync images for products
   */
  syncImages(productIds: string[], store: string = 'BHS'): Observable<any> {
    // Initialize items with pending status
    const items: ImageSyncItem[] = productIds.map(id => ({
      _id: id,
      sku: '',
      title: 'Loading...',
      status: 'pending'
    }));

    this.itemsSubject.next(items);
    this.statsSubject.next({
      total: productIds.length,
      synced: 0,
      pending: productIds.length,
      failed: 0
    });
    this.progressSubject.next({
      completed: 0,
      total: productIds.length,
      percentage: 0
    });

    return this.http.post(`${environment.API_PATH}/product/image-sync/sync`, { productIds, store });
  }

  /**
   * Handle progress update from WebSocket
   */
  updateProgress(data: { completed: number; total: number; percentage: number; type?: string }) {
    this.progressSubject.next({
      completed: data.completed,
      total: data.total,
      percentage: data.percentage
    });
  }

  /**
   * Handle individual item update from WebSocket
   */
  updateItem(data: { _id: string; sku: string; title?: string; imageUrl?: string; synced: boolean; action?: string; error?: string }) {
    const currentItems = this.itemsSubject.value;
    const itemIndex = currentItems.findIndex(item => item._id === data._id);

    if (itemIndex !== -1) {
      currentItems[itemIndex] = {
        ...currentItems[itemIndex],
        sku: data.sku,
        title: data.title || currentItems[itemIndex].title,
        status: data.synced ? 'success' : 'error',
        action: data.action as 'created' | 'updated',
        error: data.error,
        imageUrl: data.imageUrl
      };
    } else {
      // Add new item if not found
      currentItems.push({
        _id: data._id,
        sku: data.sku,
        title: data.title || 'Unknown',
        status: data.synced ? 'success' : 'error',
        action: data.action as 'created' | 'updated',
        error: data.error,
        imageUrl: data.imageUrl
      });
    }

    this.itemsSubject.next([...currentItems]);
    this.updateStats(currentItems);
  }

  /**
   * Handle status update from WebSocket
   */
  updateStatus(data: { type: string; message?: string; payload?: any }) {
    this.statusSubject.next(data);

    // If scan-completed, process the discrepancies from payload
    if (data.type === 'scan-completed' && data.payload?.discrepancies) {
      this.processDiscrepancies(data.payload);
    }
  }

  /**
   * Process discrepancies from scan-completed event
   */
  private processDiscrepancies(payload: any) {
    // Convert discrepancies to items with full data including price and tags
    const items: ImageSyncItem[] = payload.discrepancies.map((d: any) => ({
      _id: d._id,
      sku: d.sku?.production || d.sku,
      title: d.title,
      status: 'pending' as const,
      shopifyId: d.shopifyId,
      imageUrl: d.dbImageProduction,
      // Image fields
      dbImageProduction: d.dbImageProduction,
      dbImageShopify: d.dbImageShopify,
      shopifyImageUrl: d.shopifyImageUrl,
      hasImageInDB: d.hasImageInDB,
      hasImageInShopify: d.hasImageInShopify,
      imageInSync: d.imageInSync,
      shopifyMediaCount: d.shopifyMediaCount,
      // Price fields
      dbPrice: d.dbPrice,
      shopifyPrice: d.shopifyPrice,
      priceMatch: d.priceMatch,
      priceDifference: d.priceDifference,
      // Tag fields
      dbTags: d.dbTags || [],
      shopifyTags: d.shopifyTags || [],
      tagsMatch: d.tagsMatch,
      missingTagsInShopify: d.missingTagsInShopify || [],
      extraTagsInShopify: d.extraTagsInShopify || [],
      // Overall
      needsSync: d.needsSync
    }));

    this.itemsSubject.next(items);

    // Update stats
    this.statsSubject.next({
      total: payload.checkedProducts || 0,
      synced: payload.checkedProducts - payload.discrepanciesFound || 0,
      pending: payload.discrepanciesFound || 0,
      failed: 0
    });

    // Update progress to show completion
    this.progressSubject.next({
      completed: payload.checkedProducts || 0,
      total: payload.checkedProducts || 0,
      percentage: 100
    });
  }

  /**
   * Handle error from WebSocket
   */
  handleError(data: { error: string; type?: string }) {
    this.statusSubject.next({
      type: 'error',
      message: data.error
    });
  }

  /**
   * Update stats based on current items
   */
  private updateStats(items: ImageSyncItem[]) {
    const stats: ImageSyncStats = {
      total: items.length,
      synced: items.filter(item => item.status === 'success').length,
      pending: items.filter(item => item.status === 'pending' || item.status === 'syncing').length,
      failed: items.filter(item => item.status === 'error').length
    };
    this.statsSubject.next(stats);
  }

  /**
   * Mark item as syncing
   */
  setItemSyncing(productId: string) {
    const currentItems = this.itemsSubject.value;
    const itemIndex = currentItems.findIndex(item => item._id === productId);

    if (itemIndex !== -1) {
      currentItems[itemIndex].status = 'syncing';
      this.itemsSubject.next([...currentItems]);
    }
  }

  /**
   * Reset the service state
   */
  reset() {
    this.progressSubject.next({ completed: 0, total: 0, percentage: 0 });
    this.itemsSubject.next([]);
    this.statsSubject.next({ total: 0, synced: 0, pending: 0, failed: 0 });
  }

  /**
   * Get current items
   */
  getCurrentItems(): ImageSyncItem[] {
    return this.itemsSubject.value;
  }

  /**
   * Get current stats
   */
  getCurrentStats(): ImageSyncStats {
    return this.statsSubject.value;
  }

  /**
   * Cancel a running job
   */
  cancelJob(jobId: string): Observable<any> {
    return this.http.post(`${environment.API_PATH}/product/image-sync/cancel`, { jobId });
  }
}
